import { concatMap, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, catchError, exhaustMap } from 'rxjs';
import { ManagerCompaniesActionTypes } from '@appStore/managers/actions';
import { ManagerService } from '@appStore/managers/services';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import * as fromManagersStore from '@appStore/managers';
import * as actions from '@appStore/managers/actions';

@Injectable()
export class ManagerCompaniesEffects {
  constructor(
    private managerService: ManagerService,
    private actions$: Actions,
    private router: Router,
    private managersStore: Store<fromManagersStore.State>
  ) {}

  loadMyManagerCompanies$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ManagerCompaniesActionTypes.ManagerCompaniesLoadData),
      exhaustMap(action => {
        return this.managerService.getMyManagerCompanies().pipe(
          map(managerCompanies => new actions.ManagerCompaniesLoadSuccess(managerCompanies)),
          catchError(error => of(new actions.ManagerCompaniesLoadFail(error)))
        );
      })
    );
  });
  
  loadMyManagerCompaniesUsers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ManagerCompaniesActionTypes.ManagerCompaniesUsersLoadData),
      map((action: actions.ManagerCompaniesUsersLoadData) => action.payload),
      exhaustMap(indata => {
        return this.managerService.getMyManagerCompaniesUsers(indata).pipe(
          map(users => new actions.ManagerCompaniesUsersLoadSuccess(users)),
          catchError(error => of(new actions.ManagerCompaniesUsersLoadFail(error)))
        );
      })
    );
  });
  
  updateManagerCompanyUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ManagerCompaniesActionTypes.UpdateManagerCompanyUser),
      map((action: actions.UpdateManagerCompanyUser) => action.payload),
      exhaustMap(user =>
        this.managerService.updateManagerCompanyUser(user).pipe(
          map(item => new actions.UpdateManagerCompanyUserSuccess(item)),
          catchError(error => of(new actions.UpdateManagerCompanyUserFail(error)))
        )
      )
    );
  });
  
  bulkUpdateManagerCompanyUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ManagerCompaniesActionTypes.BulkUpdateManagerCompanyUser),
      map((action: actions.BulkUpdateManagerCompanyUser) => action.payload),
      exhaustMap(user =>
        this.managerService.bulkUpdateManagerCompanyUser(user).pipe(
          map(item => new actions.BulkUpdateManagerCompanyUserSuccess(item)),
          catchError(error => of(new actions.BulkUpdateManagerCompanyUserFail(error)))
        )
      )
    );
  });
  
  lockManagerCompanyUserForDelete$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ManagerCompaniesActionTypes.LockManagerCompanyUserForDelete),
      map((action: actions.LockManagerCompanyUserForDelete) => action.payload),
      exhaustMap(user =>
        this.managerService.lockManagerCompanyUserForDelete(user).pipe(
          map(item => new actions.LockManagerCompanyUserForDeleteSuccess(item)),
          catchError(error => of(new actions.LockManagerCompanyUserForDeleteFail(error)))
        )
      )
    );
  });
  
  removeManagerCompany$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ManagerCompaniesActionTypes.ManagerCompanyRemove),
      map((action: actions.ManagerCompanyRemove) => action.payload),
      exhaustMap(company =>
        this.managerService.removeManagerCompany(company).pipe(
          map(() => new actions.ManagerCompanyRemoveSuccess(company)),
          catchError(error => of(new actions.ManagerCompanyRemoveFail(error)))
        )
      )
    );
  });

  loadManagerCompanyCustomers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ManagerCompaniesActionTypes.ManagerCompanyCustomersLoadData),
      exhaustMap((action: actions.ManagerCompanyCustomersLoadData) => {
        return this.managerService.getManagerCompanyCustomers(action.payload.managerCompanyId).pipe(
          map(customers => new actions.ManagerCompanyCustomersLoadSuccess(customers)),
          catchError(error => of(new actions.ManagerCompanyCustomersLoadFail(error)))
        );
      })
    );
  });
  
  loadManagerCompanyUserCustomers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ManagerCompaniesActionTypes.ManagerCompanyUserCustomersLoadData),
      exhaustMap((action: actions.ManagerCompanyUserCustomersLoadData) => {
        return this.managerService
          .getMyManagerCompanyUserCustomers(action.payload.managerCompanyId, action.payload.userId)
          .pipe(
            map(userCustomers => new actions.ManagerCompanyUserCustomersLoadSuccess(userCustomers)),
            catchError(error => of(new actions.ManagerCompanyUserCustomersLoadFail(error)))
          );
      })
    );
  });
  
  isManagerCompanyUserExclusiveAdministrator$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ManagerCompaniesActionTypes.IsManagerCompanyUserExclusiveAdministrator),
      map((action: actions.IsManagerCompanyUserExclusiveAdministrator) => action.payload),
      exhaustMap(item =>
        this.managerService.getIsManagerCompanyUserExclusiveAdministrator(item).pipe(
          map(validItem => new actions.IsManagerCompanyUserExclusiveAdministratorSuccess(validItem)),
          catchError(error => of(new actions.IsManagerCompanyUserExclusiveAdministratorFail(error)))
        )
      )
    );
  });
  
  isManagerCompanyUserLockedForUpdate$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ManagerCompaniesActionTypes.IsManagerCompanyUserLockedForUpdate),
      map((action: actions.IsManagerCompanyUserLockedForUpdate) => action.payload),
      exhaustMap(item =>
        this.managerService.getIsManagerCompanyUserLockedForUpdate(item).pipe(
          map(validItem => new actions.IsManagerCompanyUserLockedForUpdateSuccess(validItem)),
          catchError(error => of(new actions.IsManagerCompanyUserLockedForUpdateFail(error)))
        )
      )
    );
  });
  
  getManagerCompanyUsersLockedForUpdate$ = createEffect(() => this.actions$.pipe(
    ofType(ManagerCompaniesActionTypes.ManagerCompanyUsersLockedForUpdateLoadData),
    concatMap((action: actions.ManagerCompanyUsersLockedForUpdateLoadData) => {
      return this.managerService.getManagerCompanyUsersLockedForUpdate(action.payload).pipe(
        map(users => new actions.ManagerCompanyUsersLockedForUpdateLoadSuccess(users)),
        catchError(error => of(new actions.ManagerCompanyUsersLockedForUpdateLoadFail(error)))
      );
    })
  ));

  setPrimaryContact$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ManagerCompaniesActionTypes.SetPrimaryContact),
      exhaustMap((action: actions.SetPrimaryContact) => {
        return this.managerService.setPrimaryContact(action.payload).pipe(
          map(user => new actions.SetPrimaryContactSuccess(user)),
          catchError(error => of(new actions.SetPrimaryContactFail(error)))
        );
      })
    );
  });
}
